import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

interface NavbarProps {
  onLanguageChange: (lang: string) => void;
  currentLang: string;
}

export default function Navbar({ onLanguageChange, currentLang }: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const languages = [
    { code: 'pt', label: 'PT' },
    { code: 'de', label: 'DE' },
    { code: 'en', label: 'EN' },
  ];

  const filteredCategories = categories.filter((cat) =>
    t(cat.key).toLowerCase().includes(mobileSearch.toLowerCase()),
  );

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-background-200 shadow-sm">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="text-lg md:text-xl font-bold text-foreground-950 whitespace-nowrap">
            CSSBuy Spreadsheet
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-5">
            <Link
              to="/"
              className="flex items-center gap-1.5 text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i>
              {t('nav_home')}
            </Link>

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                {t('nav_categories')}
                <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-96 bg-white rounded-xl border border-background-200 shadow-lg max-h-[480px] overflow-y-auto">
                  <div className="grid grid-cols-2 gap-1 p-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.key}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-primary-50 transition-colors"
                      >
                        <div className="w-10 h-10 shrink-0 rounded-lg overflow-hidden">
                          <img src={cat.img} alt={t(cat.key)} className="w-full h-full object-cover" />
                        </div>
                        <span className="text-sm text-foreground-800 hover:text-primary-600 transition-colors whitespace-nowrap">
                          {t(cat.key)}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/article/cssbuy-buying-guide"
              className="flex items-center gap-1.5 text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-shopping-bag-3-line"></i>
              {t('nav_guide')}
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              {t('nav_spreadsheet')}
            </a>
          </div>

          {/* Language Switcher + Mobile Menu */}
          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => onLanguageChange(lang.code)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap ${
                    currentLang === lang.code
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-foreground-900 hover:text-primary-600 transition-colors"
              aria-label="Menu"
            >
              <i className={`ri-${isOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t border-background-200 px-4 py-4 max-h-[80vh] overflow-y-auto">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Link
                to="/"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-2 px-4 py-2.5 bg-primary-50 text-primary-700 font-medium rounded-lg text-sm flex-1"
              >
                <i className="ri-home-4-line"></i>
                {t('nav_home')}
              </Link>
              <Link
                to="/article/cssbuy-buying-guide"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-2 px-4 py-2.5 bg-secondary-100 text-secondary-900 font-medium rounded-lg text-sm flex-1"
              >
                <i className="ri-shopping-bag-3-line"></i>
                {t('nav_guide')}
              </Link>
            </div>

            {/* Mobile category search */}
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                value={mobileSearch}
                onChange={(e) => setMobileSearch(e.target.value)}
                placeholder={t('search_placeholder')}
                className="w-full pl-9 pr-3 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400"
              />
            </div>

            <div className="font-medium text-sm text-foreground-900 mb-2">{t('nav_categories')}</div>
            <div className="grid grid-cols-2 gap-2">
              {filteredCategories.map((cat) => (
                <a
                  key={cat.key}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-2 py-2 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300/50 transition-all"
                >
                  <div className="w-9 h-9 shrink-0 rounded-md overflow-hidden">
                    <img src={cat.img} alt={t(cat.key)} className="w-full h-full object-cover" />
                  </div>
                  <span className="text-xs font-medium text-foreground-800 leading-tight">{t(cat.key)}</span>
                </a>
              ))}
            </div>

            <div className="border-t border-background-200 pt-3 mt-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block px-4 py-2.5 text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors"
              >
                {t('nav_spreadsheet')}
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}